/**
 * Mobile Device Restrictions Utility
 * Implements feature-based restrictions for mobile devices
 */

/**
 * Detect if the current device is mobile
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Check if a specific feature is restricted on mobile
 * @param {string} feature - Feature name ('game_launch', 'live_scoring', 'audience_display')
 * @returns {Object} Restriction info
 */
export const getFeatureRestriction = (feature) => {
  const isMobile = isMobileDevice();
  
  if (!isMobile) {
    return {
      restricted: false,
      reason: null,
      message: null
    };
  }
  
  switch (feature) {
    case 'game_launch':
      return {
        restricted: true,
        reason: 'mobile_launch_restriction',
        message: 'Game launching requires a desktop computer to control the main display for your audience.',
        icon: '💻',
        suggestions: [
          'Use a laptop or desktop computer to launch games',
          'You can still create and manage games on mobile',
          'All other features work great on mobile!'
        ]
      };
      
    case 'live_scoring':
      return {
        restricted: true,
        reason: 'mobile_scoring_restriction',
        message: 'Live game scoring requires a desktop computer for the best experience.',
        icon: '📊',
        suggestions: [
          'Use a desktop computer for live game management',
          'You can view game results on mobile',
          'Game creation and editing work on mobile'
        ]
      };
      
    case 'audience_display':
      return {
        restricted: true,
        reason: 'mobile_display_restriction',
        message: 'Audience display control requires a desktop computer.',
        icon: '🖥️',
        suggestions: [
          'Use a desktop computer to control the main display',
          'The audience display will work on a separate screen',
          'You can still manage games on mobile'
        ]
      };
      
    default:
      return {
        restricted: false,
        reason: null,
        message: null
      };
  }
};

/**
 * Get mobile-friendly message for restricted features
 * @param {string} feature - Feature name
 * @returns {string} User-friendly message
 */
export const getMobileRestrictionMessage = (feature) => {
  const restriction = getFeatureRestriction(feature);
  
  if (!restriction.restricted) {
    return null;
  }
  
  return {
    title: 'Desktop Required',
    message: restriction.message,
    icon: restriction.icon,
    suggestions: restriction.suggestions,
    cta: 'Use Desktop Computer'
  };
};

/**
 * Check if user can perform action on current device
 * @param {string} action - Action name
 * @param {string} userRole - User role
 * @returns {Object} Access result
 */
export const canPerformAction = (action, userRole) => {
  const isMobile = isMobileDevice();
  
  // League users have no restrictions
  if (userRole === 'league_user') {
    return {
      allowed: true,
      reason: null
    };
  }
  
  // Check feature-specific restrictions
  const restriction = getFeatureRestriction(action);
  
  return {
    allowed: !restriction.restricted,
    reason: restriction.restricted ? restriction.reason : null,
    message: restriction.message
  };
};


